/**
 * HOME-01 — design tokens, extracted from Afriqahome_Home_Discover.html.
 *
 * These are the approved values, not a re-eyeballing of the design. Every
 * colour and radius the redesign uses is named here so a module cannot invent
 * a near-miss shade, and so a future change to the palette is one edit rather
 * than a grep across fifteen files.
 *
 * Tailwind arbitrary values (`bg-[#F7F6F1]`) are the house style on this
 * codebase — see the existing `bg-[#F5F5F7]` / `text-[#134025]` call sites — so
 * these constants are interpolated into class strings rather than replacing
 * Tailwind with inline styles.
 */

export const HOME_TOKENS = {
  /** Page and surface backgrounds. */
  pageBg: '#F7F6F1',
  cardBg: '#FFFFFF',
  cardBorder: '#E7E6E6',
  inputBorder: '#DCDAD3',

  /** Brand. */
  brand: '#134025',
  brandHover: '#1B5533',

  /** Type. */
  textPrimary: '#2D2D2D',
  textPrimaryAlt: '#3D3D3D',
  textSecondary: '#666666',
  muted: '#8C8C86',
  mutedAlt: '#9A9A94',

  /** Spotlight badge — the paid-boost mark. */
  spotlightBg: '#FBF2DE',
  spotlightBorder: '#EAD9AC',
  spotlightText: '#7A5A0E',

  /** Verified-agent badge. Optional and badge-based — never a gate. */
  verifiedBg: '#EBF0EB',
  verifiedText: '#134025',

  /** Amenity tag. */
  amenityBg: '#F1F0EA',
  amenityText: '#4A4A45',

  /** Save heart. */
  heartSaved: '#D64545',
  heartUnsaved: '#3D3D3D',

  /** Footer. */
  footerBg: '#134025',
  footerLink: '#C9D3CB',
  footerHeading: '#FFFFFF',
  footerBoilerplate: '#8FA396',

  /** Row-header info dot. */
  infoDotBorder: '#A8A491',
} as const;

/** Display face — headings, prices, the H1. */
export const DISPLAY_FONT = "'Plus Jakarta Sans', 'Helvetica Neue', sans-serif";

/**
 * Card hover, shared by every clickable card in the redesign.
 * `transform` + `box-shadow` only, so it composes with Tailwind's own classes
 * and never fights a layout property.
 */
export const CARD_HOVER =
  'transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_26px_rgba(0,0,0,.12)]';

/** Rank-tile hover — same motion, the heavier shadow the design gives it. */
export const TILE_HOVER =
  'transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_26px_rgba(0,0,0,.16)]';

/**
 * Horizontal snap carousel.
 *
 * `hscroll` is the real scrollbar-hiding class, defined in index.css. NOT the
 * `scrollbar-hide` name used elsewhere in this repo — nothing defines that one,
 * and its call sites pair it with an inline `scrollbarWidth` that only covers
 * Firefox.
 *
 * The negative margin is the design's mobile edge-bleed: the surrounding column
 * is padded 18px, and a carousel that stopped at that padding would look like a
 * clipped list rather than a row that continues off-screen. The scroller escapes
 * the gutter and re-applies it as its own padding, so the first card still lines
 * up with the heading above it while the last one runs to the screen edge. On
 * desktop the design keeps the row inside the 64px gutter, so the bleed is
 * cancelled at `lg`.
 */
export const HSCROLL =
  'hscroll flex gap-3 lg:gap-4 overflow-x-auto snap-x snap-mandatory pb-1.5 lg:pb-2 -mx-[18px] px-[18px] lg:mx-0 lg:px-0';

/**
 * Desktop content gutter. The design puts every module inside `padding: 0 64px`
 * on a 1440 frame and 18px on the 380 frame.
 */
export const CONTENT_PAD_ONLY = 'px-[18px] lg:px-16';

/**
 * HOME-01 fix round 1 — the content column's maximum width.
 *
 * THE BUG THIS FIXES. The design was authored at a fixed 1440 frame, and the
 * first build translated its `padding: 0 64px` literally: gutters, no ceiling.
 * At 1440 that is right by coincidence. At 1920 and 2560 every row stretched to
 * fill the monitor, and the hero — whose content is a 460px search card and a
 * 280px proof card pinned to the left gutter — became those two cards floating
 * in a metre of empty brand green. The page did not look wide, it looked broken.
 *
 * 1440 is the ceiling rather than 1520 because it is the width the design was
 * drawn at: every gap, card width and type size in this redesign was chosen
 * against that frame, and a column wider than the drawing is a layout nobody
 * has looked at. Beyond it the column centres and the surplus becomes margin —
 * or, for the hero, more photograph.
 */
export const CONTENT_MAX = 'max-w-[1440px]';

/**
 * The shell every home surface sits in: centred, capped, and gutter-padded.
 *
 * Header, hero content, each discovery row, All properties and the footer all
 * use THIS — one class string, so nothing can be centred to a different width
 * than the thing above it. That consistency is the fix; the cap alone would
 * still leave a header at 1280 over a feed at 1440.
 */
export const CONTENT_SHELL = `mx-auto w-full ${CONTENT_MAX} ${CONTENT_PAD_ONLY}`;

/**
 * Kept as the name the modules already import, now carrying the cap.
 * A page-level wrapper that was `px-[18px] lg:px-16` becomes centred and capped
 * without every call site changing.
 */
export const CONTENT_PAD = CONTENT_SHELL;

/**
 * Hero height. `min(560px, 62vh)` rather than a flat 560:
 *
 *   560px  the design's own figure, and what a 1440×900 desktop gets.
 *   62vh   the ceiling on a short viewport. A laptop at 1280×720, or any
 *          browser with a bookmarks bar and a downloads shelf, gave 560px of
 *          hero to a 620px viewport — the entire fold was hero, and the rows
 *          the page exists to show began below it.
 *
 * Whichever is smaller wins, so the hero can never take more than about
 * three-fifths of the fold. Mobile has no hero at all.
 */
export const HERO_HEIGHT_CSS = 'min(560px, 62vh)';

/**
 * Visible keyboard focus, one definition.
 *
 * `focus-visible` rather than `focus`, so a mouse click does not leave a ring
 * behind but a Tab press always does. The offset keeps the ring off the
 * control's own border, which on the brand-green buttons would otherwise be
 * invisible against it.
 */
export const FOCUS_RING =
  'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#134025] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F7F6F1]';

/** The same ring on a dark (brand-green) ground — the footer, the hero. */
export const FOCUS_RING_ON_BRAND =
  'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#134025]';

/**
 * HOME-01 fix round 1 — clearance for the floating bottom nav.
 *
 * components/BottomNav.tsx is `fixed bottom-0`, `md:hidden`, and draws a
 * rounded bar with `mb-2` (8px) around a ~56px row, plus
 * `env(safe-area-inset-bottom)` for the iOS home indicator. Scrollable content
 * that ends flush with the viewport therefore ends UNDERNEATH it: in Dirie's
 * screenshots the last card's price and title sat behind the bar.
 *
 * 96px is the bar (56) + its margin (8) + a card's own breathing room (32),
 * and the safe-area inset is added on top rather than assumed to be inside it —
 * on a notched iPhone the inset moves the bar UP, so it eats more of the page,
 * not less. Safari's dynamic toolbar changes the viewport height, never this
 * distance, so one value covers collapsed and expanded alike.
 *
 * `lg:pb-0` — the nav is `md:hidden`, so from `md` up there is nothing to clear.
 * The class stops at `md` to match the nav's own breakpoint exactly.
 */
export const BOTTOM_NAV_CLEARANCE =
  'pb-[calc(96px+env(safe-area-inset-bottom))] md:pb-0';
